import React, { useMemo, useState, useEffect, useCallback, useContext } from "react";
import { TransformWrapper, TransformComponent } from "react-zoom-pan-pinch";

import { navigate } from "gatsby-link";

import { styled } from '@mui/material/styles';

import _ from "lodash";

import Player from '../Player';
import FullscreenWrapper from '../FullscreenWrapper';

import { getImage, GatsbyImage } from "gatsby-plugin-image";

import { Box, Grow, IconButton } from "@mui/material";

import Carousel from '../Carousel/Carousel';

import { getEntityUrl } from '../../util';

import './imageslider-advanced.css';
import { Clear } from "@mui/icons-material";
import FormatAlignLeftIcon from "@mui/icons-material/FormatAlignLeft";

import { USE_FASTSEEK, useOtaSync } from "../../hooks/ota-sync";
import { TourContext } from "../Tour/TourContext";
import {LocalizedString} from "../Strings";
import {OverlayIcon} from "../Overlay/OverlayIcon";
import {microtype} from "../../util/microtypography";

export const selectAudioUrl = (component, files, syncSlug) => {
    if (component.audioList && component.audioList.length > 0 && syncSlug && syncSlug !== component.syncSlug) {
        const audio = _.find(component.audioList, { syncSlug: syncSlug });
        return _.find(files.nodes, { id: audio?.audio.localFile___NODE })?.publicURL;
    }

    return _.find(files.nodes, { id: component.audio?.localFile___NODE })?.publicURL;
};

const TranscriptionDiv = styled('div', { shouldForwardProp: () => true })(() => {
    return {
        margin: "auto",
        width: "100%",
        height: '100%',
        zIndex: 4,
        padding: '8px',
        paddingTop: 60,
        position: "absolute",
        top: 0,
        overflow: "auto",
        backgroundColor: "var(--background-color)"
    }
});

const OverlayToggle = styled('div')(() => ({
    display: 'block',
    position: 'absolute',
    bottom: '2.7em',
    left: '.5em',
    zIndex: 3,
    color: '#fff',
    lineHeight: 1,
    padding: '.5em',
    background: 'rgb(0 0 0 / 34%)',
    borderRadius: '100%',
    margin: 0,
    width: '2.5em',
    height: '2.5em',
}));

const ImagesliderAdvanced = (props) => {
    const { component: { height, allowFullscreen, ...component }, files, pageContext: { defaultLocale } } = props;
    const [fullscreenMode, setFullscreenMode] = useState(component.fullscreen || false);
    const [syncing, setSyncing] = useState(component.syncSlug || component.syncStation);
    const playerRef = React.createRef();
    const { isTour, skipToNext } = useContext(TourContext);

    // ImagesliderAdvanced uses showTimecontrols instead of showTimeControls
    component.showTimeControls = component.showTimecontrols;

    const images = useMemo(() => {
        const images = [];
        _.each(component.image,
            ({ image, caption, overlayText, objectFit, objectPositionX, objectPositionY }) => {
                images.push({
                    localFile: _.find(files.nodes, { id: image?.localFile___NODE }),
                    caption,
                    overlayText,
                    objectFit,
                    objectPositionX,
                    objectPositionY,
                    id: image.id
                })
            });
        return images;
    }, [files, component]);

    const [ selectedAudioUrl, setSelectedAudioUrl ] = useState(selectAudioUrl(component, files));

    const [caption, setCaption] = useState('');
    const [overlayText, setOverlayText] = useState('');
    const [isError, setIsError] = useState(false);

    const onCarouselChange = useCallback((i) => {
        if (images && images[i]) {
            setCaption(images[i].caption);
            setOverlayText(images[i].overlayText);
        }
    }, [images, setCaption, setOverlayText]);

    const zoomImages = useMemo(() => {
        return false;
        // see https://gitlab.com/linon/erwin/erwinapp/-/issues/165
        // return component.zoomable;
    }, []);

    const carouselProps = {
        infiniteLoop: true,
        fullHeightHover: false,
        interval: 0,
        autoPlay: false,
        showThumbs: false,
        showStatus: false,
        swipeScrollTolerance: 30,
        swipeable: false,
        showArrows: images?.length > 1,
        onChange: onCarouselChange,
    };

    const [zoomRef, setZoomRef] = useState(null);
    const zoomRefCb = useCallback((node) => {
        setZoomRef(node);
    }, [setZoomRef]);

    //TODO - use zoomWrapper
    useEffect(() => {
        const centerZoom = () => {
            setTimeout(() => { if (zoomRef) zoomRef.centerView() }, 50);
        }

        window.addEventListener('resize', centerZoom);
        return () => window.removeEventListener('resize', centerZoom)
    }, [zoomRef, onCarouselChange]);

    useEffect(() => {
        onCarouselChange(0);
    }, []);//init

    useEffect(() => {
        if (zoomRef) zoomRef.centerView()
    }, [zoomRef, fullscreenMode]);

    const onImageLoad = useCallback(() => { if (zoomRef) zoomRef.centerView(undefined, 1) }, [zoomRef]);

    const zoomWrapperStyle = { width: '100%', margin: '0 auto' };
    if (fullscreenMode) {
        zoomWrapperStyle.maxHeight = '-webkit-fill-available';
        if (selectedAudioUrl) {
            zoomWrapperStyle.height = '96vh';
        } else {
            zoomWrapperStyle.height = '100vh';
        }
    } else {
        zoomWrapperStyle.maxHeight = 'none';
        zoomWrapperStyle.height = 'auto';
    }

    const onEnded = () => {
        console.debug('onEnded', fullscreenMode)
        if (isTour) skipToNext();
        else if (component.continueTo) navigate(getEntityUrl({ entity: component.continueTo, defaultLocale }));
        else if (fullscreenMode && component.fullscreen) navigate(-1);
    }

    const onError = () => {
        setIsError(true);
    }

    const [overlayTextHidden, setOverlayTextHidden] = useState(true);
    const onOverlayToggle = useCallback((e) => {
        setOverlayTextHidden(!overlayTextHidden);
        e.preventDefault();
        e.stopPropagation();
    }, [overlayTextHidden]);

    const [showTranscription, setShowTranscription] = useState(false);
    const toggleShowTranscription = useCallback((e) => {
        setShowTranscription(!showTranscription);
        e.preventDefault();
        e.stopPropagation();
    }, [showTranscription]);

    /**
     * We need a local state for fullscreen to be able to detect which component is in fullscreen mode
     * if there are several components on the page.
     * Otherwise, the elements will overlap and the player of the other component will be visible instead of the current one.
     */
    const toggleFullscreenCallback = useCallback((isFullscreen) => {
        setFullscreenMode(isFullscreen);
    });

    const otaSyncCallback = useCallback(({stationId, slug, seekTo}) => {
        const internalPlayer = playerRef.current?.getInternalPlayer();

        let audioUrl = selectAudioUrl(component, files, slug);
        if (!audioUrl) {
            console.warn('no audioUrl found for slug ' + slug);
            return;
        }

        setSyncing(true)
        setSelectedAudioUrl(audioUrl);

        if (internalPlayer.paused) {
            internalPlayer.play();
        }

        if (USE_FASTSEEK && internalPlayer?.fastSeek) {
            internalPlayer.fastSeek(seekTo);
        } else {
            internalPlayer.currentTime = seekTo;
        }
    });

    useOtaSync({ playerRef, component, playCallback: otaSyncCallback });

    return (<div className="imageslider-advanced" >
        <FullscreenWrapper {...props} showFullscreenButton={allowFullscreen && !showTranscription} toggleFullscreenCallback={toggleFullscreenCallback}>
            {
                images.length > 0 && (
                    <div className="flex-div">
                        <div className="imageslider-content-container">
                            {component.transcription &&
                                <Grow in={showTranscription} timeout={600}>
                                    <TranscriptionDiv className="transcription">
                                        <IconButton onClick={toggleShowTranscription} style={{ position: 'sticky', top: '0', right: '0', zIndex: 1, float: 'right', background: 'var(--background-color)'}}><Clear /></IconButton>
                                        <Box mb={fullscreenMode ? '2em' : '0'}>
                                            <Box dangerouslySetInnerHTML={{ __html: microtype(component.transcription, props.entity.locale || defaultLocale) }} />
                                        </Box>
                                    </TranscriptionDiv>
                                </Grow>
                            }
                            <div className="imageslider-content">
                                <div className="vertically-centered-image-container" style={{position: 'relative'}}>
                                    {overlayText && (
                                        <OverlayToggle className="overlay-toggle" onClick={onOverlayToggle} ><OverlayIcon fontSize="medium" /></OverlayToggle>
                                    )}
                                    <Carousel carouselProps={carouselProps} className="imageslider" fullscreenMode={fullscreenMode} hasCaption={true}>
                                        {images.map((image, n) =>
                                            <div key={n} className="carousel-child">
                                                {overlayText && (
                                                    <div className="richtext overlay-text" style={{ opacity: overlayTextHidden ? '0' : '0.8' }} dangerouslySetInnerHTML={{ __html: microtype(overlayText, props.entity.locale || defaultLocale) }} />
                                                )}
                                                <div align="center">
                                                    {zoomImages ? (<TransformWrapper centerOnInit={false} disabled={zoomImages !== true} ref={zoomRefCb} >
                                                        <TransformComponent wrapperStyle={zoomWrapperStyle} wrapperClass='zoomWrapper'>
                                                            <GatsbyImage
                                                                onLoad={onImageLoad}
                                                                image={getImage(image.localFile)}
                                                                alt=""
                                                                objectFit={image.objectFit || "contain"}
                                                                objectPosition={`${image.objectPositionX || "center"} ${image.objectPositionY || "center"}`}
                                                                loading="eager"
                                                                imgStyle={{ position: 'static', height: `${height}vh`, verticalAlign: 'center' }}
                                                            />
                                                        </TransformComponent>
                                                    </TransformWrapper>) :
                                                        <GatsbyImage
                                                            onLoad={onImageLoad}
                                                            image={getImage(image.localFile)}
                                                            alt=""
                                                            objectFit={image.objectFit || "contain"}
                                                            objectPosition={`${image.objectPositionX || "center"} ${image.objectPositionY || "center"}`}
                                                            loading="eager"
                                                            imgStyle={{ position: 'static', height: `${height}vh`, verticalAlign: 'center' }}
                                                        />
                                                    }
                                                </div>
                                            </div>
                                        )}
                                    </Carousel >
                                </div>
                            </div>

                            {caption !== '' &&
                                <div className="richtext caption-inline" dangerouslySetInnerHTML={{ __html: microtype(caption, props.entity.locale || defaultLocale) }}></div>
                            }
                        </div>

                        {isError && component.onSiteOnly &&
                            <LocalizedString className={"error"} string={(component.onSiteOnly ? "This audio is only available on site" : "")} locale={props.entity.locale || defaultLocale} />
                        }

                        {selectedAudioUrl &&
                            <div className="player-div">
                                <Player
                                    url={selectedAudioUrl}
                                    showTimeControls={component.showTimeControls !== null ? component.showTimeControls : true}
                                    loop={ (!isTour && component.loop) || false}
                                    playing={component.autoplayAudio || false}
                                    playsinline={component.fullscreen}
                                    onEnded={onEnded}
                                    onError={onError}
                                    playerBarContent={component.transcription && <IconButton onClick={toggleShowTranscription}>{showTranscription ? <Clear/> : <FormatAlignLeftIcon/>}</IconButton>}
                                    ref={playerRef}
                                    enableControls={true}
                                    syncing={syncing}
                                />
                            </div>
                        }
                    </div>
                )
            }

        </FullscreenWrapper >
    </div>
    );
}

export default ImagesliderAdvanced;
